<template>
	<div class="Blurb" :class="classes">
		<div v-if="image.length" class="Blurb-image">
			<img :src="image.src" :alt="image.alt" :title="image.title" />
		</div>

		<div v-if="tag" class="Blurb-tag">
			<span>{{ tag }}</span>
		</div>

		<div v-if="heading" class="Blurb-heading">
			<h1>{{ heading }}</h1>
		</div>

		<div v-if="subheading" class="Blurb-subHeading">
			<p>{{ subheading }}</p>
		</div>

		<div v-if="meta.length" class="Blurb-meta">
			<span v-for="item in meta" :key="item.text">{{ item.text }}</span>
		</div>

		<div v-if="excerpt" class="Blurb-excerpt">
			<p>{{ excerpt }}</p>
		</div>

		<div v-if="link.length" class="Blurb-link">
			<a :href="url" class="Button" :class="link.isButton ? link.buttonClasses : ''">{{ link.text }}</a>
		</div>

		<div v-if="action.length" class="Blurb-action">
			<a :href="url" class="Button" :class="action.buttonClasses">{{ action.text }}</a>
		</div>

		<p v-if="finePrint" class="Blurb-finePrint" v-html="fineprint"></p>
	</div>
</template>

<script>
export default {
	name: 'CloBlurb',
	props: {
		classes: {
			type: String,
			default: '',
		},
		image: {
			type: Object,
			default() {
				return {};
			},
		},
		tag: {
			type: String,
			default: '',
		},
		heading: {
			type: String,
			default: '',
		},
		subheading: {
			type: String,
			default: '',
		},
		meta: {
			type: Array,
			default() {
				return [];
			},
		},
		excerpt: {
			type: String,
			default: '',
		},
		link: {
			type: Object,
			default() {
				return {};
			},
		},
		action: {
			type: Object,
			default() {
				return {};
			},
		},
		finePrint: {
			type: String,
			default: '',
		},
	},
};
</script>

<style lang="scss">
/* --------------------------------
	Blurb styles
-------------------------------- */

.Blurb {
	display: flex;
	flex-wrap: wrap;
	.Blurb-image,
	.Blurb-tag,
	.Blurb-heading,
	.Blurb-meta,
	.Blurb-excerpt,
	.Blurb-link {
		flex: 1 1 100%;
	}
	// & div:last-child {
	// 	margin-bottom: 0;
	// }
}

.Blurb-image {
	@include stack-md;
	max-width: 100%;
}

.Blurb-tag {
	color: $color-text-muted;
	font-size: $text-size-xs;
	margin: 0;
	text-transform: uppercase;
}

.Blurb-heading {
	@include stack-xs;
	h1,
	h2 {
		@include h2;
		margin: 0;
		// line-height: 1.2;
	}
	h3,
	h4,
	h5,
	h6 {
		@include h3;
		margin: 0;
		// line-height: 1.2;
	}
}

.Blurb-meta {
	@include stack-xs;
	color: $color-text-muted;
	font-size: $text-size-sm;
	& :not(:first-child) {
		&::before {
			content: '|'; /* separator between meta items */
			color: $color-neutral-2;
			margin-left: $space-xs;
			margin-right: $space-sm;
			[dir='rtl'] & {
				margin-left: $space-sm;
				margin-right: $space-xs;
			}
		}
	}
}

.Blurb-excerpt {
	@include stack-sm;
	font-size: $text-size-md;
	line-height: 1.6;
}

.Blurb-link {
	@include stack-sm;
	align-items: flex-start;
	font-size: $text-size-md;
	line-height: 1.6;
	display: flex;
	flex-wrap: wrap;
	.Button {
		@include inline-sm;
		flex: 0 1 auto;
		&:not(:last-child) {
			margin-bottom: $space-sm;
		}
	}
	&--center {
		justify-content: space-around;
	}
}

/* ----------------
	Variants
---------------- */

/* Small variant */
@mixin Blurb--small {
	.Blurb-heading {
		@include stack-xs;
		h1,
		h2 {
			@include h3;
			margin: 0;
		}
		h3,
		h4,
		h5,
		h6 {
			@include h4;
			margin: 0;
		}
	}

	.Blurb-meta {
		@include stack-xs;
		font-size: $text-size-xs;
	}

	.Blurb-excerpt {
		@include stack-sm;
		font-size: $text-size-sm;
		line-height: 1.4;
	}

	.Blurb-link {
		@include stack-xs;
		font-size: $text-size-sm;
		line-height: 1.4;
	}
}

.Blurb--small {
	@include Blurb--small;
}

/* Tiny variant */
@mixin Blurb--tiny {
	.Blurb-heading {
		@include stack-xs;
		h1,
		h2 {
			@include h4;
			margin: 0;
		}
		h3,
		h4,
		h5,
		h6 {
			@include h5;
			margin: 0;
		}
	}

	.Blurb-meta {
		@include stack-xs;
		font-size: $text-size-xs;
	}

	.Blurb-excerpt {
		@include stack-xs;
		font-size: $text-size-xs;
		line-height: 1.3;
	}

	.Blurb-link {
		@include stack-xs;
		font-size: $text-size-xs;
		line-height: 1.3;
	}
}

.Blurb--tiny {
	@include Blurb--tiny;
}

/* Large variant - declared as a mixin for subsequent Responsive variant */
@mixin Blurb--large {
	.Blurb-heading {
		@include stack-sm;
		h1,
		h2 {
			@include h1;
		}
		h3,
		h4,
		h5,
		h6 {
			@include h2;
		}
	}

	.Blurb-image {
		@include stack-md;
	}

	.Blurb-tag {
		@include stack-xs;
		font-size: $text-size-sm;
	}

	.Blurb-meta {
		// @include stack-md;
		font-size: $text-size-md;
	}

	.Blurb-excerpt {
		@include stack-sm;
		font-size: $text-size-md;
		line-height: 1.8;
	}

	.Blurb-link {
		@include stack-sm;
		font-size: $text-size-lg;
		// line-height: 1.8;
	}
}

.Blurb--large {
	@include breakpoint(large) {
		@include Blurb--large;
	}
}

/* Hero variant - declared as a mixin for subsequent Responsive variant */
@mixin Blurb--hero {
	.Blurb-heading {
		@include stack-sm;
		h1,
		h2 {
			@include h1;
			font-size: 2.25rem;
			@include breakpoint(large) {
				font-size: 2.75rem;
			}
		}
		h3,
		h4,
		h5,
		h6 {
			@include h2;
		}
	}

	.Blurb-image {
		@include stack-md;
	}

	.Blurb-tag {
		@include stack-xs;
		font-size: $text-size-sm;
	}

	.Blurb-meta {
		// @include stack-md;
		font-size: $text-size-md;
	}

	.Blurb-excerpt {
		@include stack-md;
		font-size: $text-size-lg;
		// line-height: 1.8;
	}

	.Blurb-link {
		@include stack-md;
		font-size: $text-size-lg;
		// line-height: 1.8;
		.Button {
			@include inline-md;
			&:not(:last-child) {
				margin-bottom: $space-md;
			}
		}
	}
}

.Blurb--hero {
	@include Blurb--large;
	@include breakpoint(large) {
		@include Blurb--hero;
	}
}

/* Heading variant - for use in page headings */
.Blurb--heading {
	@include Blurb--large;
	display: flex;
	flex-wrap: wrap;
	.Blurb-heading {
		flex: 1 1 100%;
		@include stack-sm;
	}
	.Blurb-meta {
		@include stack-sm;
		flex: 0 1 auto;
	}
	.Blurb-excerpt {
		color: $color-neutral-4;
		flex: 1 1 100%;
		font-style: italic;
		order: 1;
	}
	.Blurb-link {
		@include stack-sm;
		flex: 1 1 auto;
		justify-content: flex-end;
	}
	@include breakpoint(medium) {
		// @include stack-lg;
		.Blurb-heading {
			@include stack-sm;
		}
		.Blurb-meta {
			@include stack-md;
			font-size: $text-size-md;
		}
		.Blurb-excerpt {
			font-size: $text-size-lg;
			font-style: italic;
			margin: 0;
		}
	}
}

/* Centered variant */
.Blurb--centered {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-around;
	text-align: center;
	> div {
		flex: 1 1 100%;
	}
	.Blurb-heading {
		justify-content: space-around;
		display: flex;
		flex: 1 1 auto;
	}
	.Blurb-image {
		img,
		> div {
			margin: 0 auto;
		}
	}
	.Blurb-link {
		justify-content: center;
		display: flex;
	}
}

.Blurb--centeredSmall {
	@media (max-width: 768px) {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-around;
		text-align: center;
		> div {
			flex: 1 1 100%;
		}
		.Blurb-heading {
			justify-content: space-around;
			display: flex;
			flex: 1 1 auto;
		}
		.Blurb-image {
			img,
			> div {
				margin: 0 auto;
			}
		}
		.Blurb-link {
			justify-content: center;
			display: flex;
		}
	}
}

/* Inline variant for all elements to flow in a 
	horizonal, wrapping row with flex */
.Blurb--inline {
	align-items: center;
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	.Blurb-image,
	.Blurb-tag,
	.Blurb-heading,
	.Blurb-meta,
	.Blurb-excerpt,
	.Blurb-link {
		flex: 0 1 auto;
		margin: $space-xs $space-sm $space-xs $space-sm;
	}
	.Blurb-link {
		.Button {
			margin: 0;
		}
	}
	.Blurb-heading,
	.Blurb-excerpt {
		// @include inset-squish-md;
		flex: 0 1 auto;
	}
}

/* Reverse variant for dark backgrounds */
.Blurb--reverse {
	@include colorReverse;
	.Blurb-tag {
		color: $color-text-muted-reverse;
	}
	.Blurb-meta {
		color: $color-text-muted-reverse;
		& :not(:first-child) {
			&::before {
				color: $color-text-muted-reverse;
			}
		}
	}
	.Blurb-excerpt {
		color: $color-text-reverse;
	}
}

/* Blurb with a lot of emphasis */
.Blurb--callout,
.Blurb--calloutMinor {
	@include inset-stretch-lg;
	display: block;
	position: relative;
	border-radius: $radius-sm;
	text-align: center;
	background-color: $color-primary-0;
	margin-bottom: $space-lg;
	@include breakpoint(medium) {
		@include inset-squish-xl;
		margin-bottom: $space-xl;
	}
	.Blurb-heading * {
		margin: $space-stack-sm;
	}
	.Blurb-subHeading * {
		margin: $space-stack-md;
	}
	.Blurb-excerpt * {
		@include text(3);
		font-weight: normal;
		margin: $space-stack-lg;
	}
	.Blurb-excerpt--muted * {
		font-weight: normal;
		color: $color-text-muted;
	}
	.Blurb-finePrint {
		@include text(2);
		color: $color-text;
	}
	.Blurb-action {
		text-align: center;
		margin: $space-stack-md;
		&:last-child {
			margin: 0;
		}
	}
}

.Blurb--callout {
	@include dropshadow-2;
	.Blurb-heading * {
		@include text(7);
		margin: $space-stack-md;
	}
	.Blurb-subHeading * {
		@include text(5);
		font-weight: bold;
		margin: $space-stack-md;
	}
	.Blurb-excerpt * {
		@include text(4);
	}
}
</style>
